import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    Paper,
    Group,
    Text,
    Button,
    Table,
    Badge,
    Modal,
    Stack,
    TextInput,
    Switch,
    ActionIcon,
    Tooltip,
    NumberInput,
    Select,
    Chip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
    IconPlus,
    IconPencil,
    IconTrash,
    IconCopy,
    IconSearch,
    IconX,
} from "@tabler/icons-react";
import type {
    TransportDepartureDto,
    TransportRouteDto,
    CreateTransportDepartureRequest,
} from "../types";

const apiUrl = process.env.REACT_APP_API_URL;
const RED = "#ff5e5e";

type Props = {
    eventId: number | null;
    routes: TransportRouteDto[];
    departures: TransportDepartureDto[];
    refreshDepartures: () => Promise<void>;
    onDeleteDeparture: (id: number) => void;
};

function formatDateToYmd(date: Date | null) {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export default function TransportRouteDeparturesTab({
    eventId,
    routes,
    departures,
    refreshDepartures,
    onDeleteDeparture,
}: Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TransportDepartureDto | null>(null);

    const [transportRouteId, setTransportRouteId] = useState<string | null>(null);
    const [departureDate, setDepartureDate] = useState("");
    const [startTime, setStartTime] = useState("10:00");
    const [capacity, setCapacity] = useState<number>(20);
    const [vehicleId, setVehicleId] = useState<number | null>(null);
    const [status, setStatus] = useState("active");
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);

    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copySource, setCopySource] = useState<TransportDepartureDto | null>(null);
    const [copyDepartureDate, setCopyDepartureDate] = useState("");
    const [copyStartTime, setCopyStartTime] = useState("");
    const [copySaving, setCopySaving] = useState(false);

    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkTransportRouteId, setBulkTransportRouteId] = useState<string | null>(null);
    const [bulkDepartureDate, setBulkDepartureDate] = useState("");
    const [bulkStartTime, setBulkStartTime] = useState("10:00");
    const [bulkEndTime, setBulkEndTime] = useState("18:00");
    const [bulkIntervalMinutes, setBulkIntervalMinutes] = useState<number>(60);
    const [bulkCapacity, setBulkCapacity] = useState<number>(20);
    const [bulkVehicleId, setBulkVehicleId] = useState<number | null>(null);
    const [bulkSaving, setBulkSaving] = useState(false);

    const [filterDate, setFilterDate] = useState<string>("all");
    const [filterRouteId, setFilterRouteId] = useState<string | null>("all");
    const [filterStatus, setFilterStatus] = useState<string | null>("all");
    const [filterActive, setFilterActive] = useState<string | null>("all");
    const [search, setSearch] = useState("");

    const routeOptions = routes
        .filter((r) => r.isActive)
        .map((r) => ({
            value: String(r.transportRouteID),
            label: r.nameDK,
        }));

    const filterRouteOptions = [
        { value: "all", label: "Alle ruter" },
        ...routes.map((r) => ({
            value: String(r.transportRouteID),
            label: r.nameDK,
        })),
    ];

    const dateOptions = useMemo(() => {
        const uniqueDates = Array.from(
            new Set(departures.map((d) => d.departureDate).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));

        return uniqueDates;
    }, [departures]);

    const filterStatusOptions = [
        { value: "all", label: "Alle statusser" },
        { value: "active", label: "active" },
        { value: "cancelled", label: "cancelled" },
        { value: "closed", label: "closed" },
    ];

    const filterActiveOptions = [
        { value: "all", label: "Alle" },
        { value: "true", label: "Kun aktive" },
        { value: "false", label: "Kun inaktive" },
    ];

    const filteredDepartures = useMemo(() => {
        const q = search.trim().toLowerCase();

        return [...departures]
            .filter((departure) => {
                if (filterDate !== "all" && departure.departureDate !== filterDate) {
                    return false;
                }

                if (
                    filterRouteId &&
                    filterRouteId !== "all" &&
                    String(departure.transportRouteID) !== filterRouteId
                ) {
                    return false;
                }

                if (
                    filterStatus &&
                    filterStatus !== "all" &&
                    (departure.status ?? "").toLowerCase() !== filterStatus.toLowerCase()
                ) {
                    return false;
                }

                if (filterActive === "true" && !departure.isActive) {
                    return false;
                }

                if (filterActive === "false" && departure.isActive) {
                    return false;
                }

                if (q) {
                    const haystack = [
                        departure.routeName,
                        departure.departureDate,
                        departure.startTime,
                        departure.status,
                        departure.capacity?.toString(),
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();

                    if (!haystack.includes(q)) {
                        return false;
                    }
                }

                return true;
            })
            .sort((a, b) => {
                const byDate = (a.departureDate ?? "").localeCompare(b.departureDate ?? "");
                if (byDate !== 0) return byDate;

                const byTime = (a.startTime ?? "").localeCompare(b.startTime ?? "");
                if (byTime !== 0) return byTime;

                return (a.routeName ?? "").localeCompare(b.routeName ?? "");
            });
    }, [departures, filterDate, filterRouteId, filterStatus, filterActive, search]);

    const resetFilters = () => {
        setFilterDate("all");
        setFilterRouteId("all");
        setFilterStatus("all");
        setFilterActive("all");
        setSearch("");
    };

    const openCreate = () => {
        setEditing(null);
        setTransportRouteId(routeOptions[0]?.value ?? null);
        setDepartureDate("");
        setStartTime("10:00");
        setCapacity(20);
        setVehicleId(null);
        setStatus("active");
        setIsActive(true);
        setModalOpen(true);
    };

    const openEdit = (departure: TransportDepartureDto) => {
        setEditing(departure);
        setTransportRouteId(String(departure.transportRouteID));
        setDepartureDate(departure.departureDate);
        setStartTime(departure.startTime);
        setCapacity(departure.capacity);
        setVehicleId(departure.vehicleID ?? null);
        setStatus(departure.status ?? "active");
        setIsActive(departure.isActive);
        setModalOpen(true);
    };

    const openCopy = (departure: TransportDepartureDto) => {
        setCopySource(departure);
        setCopyDepartureDate(departure.departureDate);
        setCopyStartTime(departure.startTime);
        setCopyModalOpen(true);
    };

    const openBulkCreate = () => {
        setBulkTransportRouteId(routeOptions[0]?.value ?? null);
        setBulkDepartureDate("");
        setBulkStartTime("10:00");
        setBulkEndTime("18:00");
        setBulkIntervalMinutes(60);
        setBulkCapacity(20);
        setBulkVehicleId(null);
        setBulkModalOpen(true);
    };

    const save = async () => {
        if (!eventId) return;

        if (!transportRouteId) {
            showNotification({
                title: "Mangler",
                message: "Vælg rute",
                color: "red",
            });
            return;
        }

        if (!departureDate.trim()) {
            showNotification({
                title: "Mangler",
                message: "Dato er påkrævet (yyyy-MM-dd)",
                color: "red",
            });
            return;
        }

        if (!startTime.trim()) {
            showNotification({
                title: "Mangler",
                message: "Starttid er påkrævet (HH:mm)",
                color: "red",
            });
            return;
        }

        try {
            setSaving(true);

            if (!editing) {
                const payload: CreateTransportDepartureRequest = {
                    eventID: eventId,
                    transportRouteID: Number(transportRouteId),
                    departureDate: departureDate.trim(),
                    startTime: startTime.trim(),
                    vehicleID: vehicleId,
                    capacity,
                };

                await axios.post(`${apiUrl}/api/transport-route/admin/departures`, payload);

                showNotification({
                    title: "Gemt",
                    message: "Afgang oprettet",
                });
            } else {
                await axios.put(
                    `${apiUrl}/api/transport-route/admin/departures/${editing.transportDepartureID}`,
                    {
                        departureDate: departureDate.trim(),
                        startTime: startTime.trim(),
                        vehicleID: vehicleId,
                        capacity,
                        status,
                        isActive,
                    }
                );

                showNotification({
                    title: "Gemt",
                    message: "Afgang opdateret",
                });
            }

            setModalOpen(false);
            await refreshDepartures();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunde ikke gemme afgang",
                color: "red",
            });
        } finally {
            setSaving(false);
        }
    };

    const copyDeparture = async () => {
        if (!copySource) return;

        if (!copyDepartureDate.trim()) {
            showNotification({
                title: "Mangler",
                message: "Dato er påkrævet (yyyy-MM-dd)",
                color: "red",
            });
            return;
        }

        if (!copyStartTime.trim()) {
            showNotification({
                title: "Mangler",
                message: "Starttid er påkrævet (HH:mm)",
                color: "red",
            });
            return;
        }

        try {
            setCopySaving(true);

            await axios.post(
                `${apiUrl}/api/transport-route/admin/departures/${copySource.transportDepartureID}/copy`,
                {
                    departureDate: copyDepartureDate.trim(),
                    startTime: copyStartTime.trim(),
                }
            );

            showNotification({
                title: "Gemt",
                message: "Afgang kopieret",
            });

            setCopyModalOpen(false);
            await refreshDepartures();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke kopiere afgang",
                color: "red",
            });
        } finally {
            setCopySaving(false);
        }
    };

    const bulkCreateDepartures = async () => {
        if (!eventId) return;

        if (!bulkTransportRouteId) {
            showNotification({
                title: "Mangler",
                message: "Vælg rute",
                color: "red",
            });
            return;
        }

        if (!bulkDepartureDate.trim()) {
            showNotification({
                title: "Mangler",
                message: "Dato er påkrævet (yyyy-MM-dd)",
                color: "red",
            });
            return;
        }

        if (!bulkStartTime.trim() || !bulkEndTime.trim()) {
            showNotification({
                title: "Mangler",
                message: "Fra- og til-tid er påkrævet (HH:mm)",
                color: "red",
            });
            return;
        }

        if (bulkIntervalMinutes < 1) {
            showNotification({
                title: "Fejl",
                message: "Interval skal være mindst 1 minut",
                color: "red",
            });
            return;
        }

        try {
            setBulkSaving(true);

            await axios.post(`${apiUrl}/api/transport-route/admin/departures/bulk`, {
                eventID: eventId,
                transportRouteID: Number(bulkTransportRouteId),
                departureDate: bulkDepartureDate.trim(),
                startTime: bulkStartTime.trim(),
                endTime: bulkEndTime.trim(),
                intervalMinutes: bulkIntervalMinutes,
                vehicleID: bulkVehicleId,
                capacity: bulkCapacity,
            });

            showNotification({
                title: "Gemt",
                message: "Afgange oprettet",
            });

            setBulkModalOpen(false);
            await refreshDepartures();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke masseoprette afgange",
                color: "red",
            });
        } finally {
            setBulkSaving(false);
        }
    };

    return (
        <>
            <Group justify="space-between" mb="sm">
                <Text fw={700}>Afgange</Text>

                <Group>
                    <Button
                        variant="default"
                        onClick={openBulkCreate}
                        disabled={!eventId || routeOptions.length === 0}
                    >
                        Masseopret
                    </Button>

                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={openCreate}
                        disabled={!eventId || routeOptions.length === 0}
                        style={{ backgroundColor: RED }}
                    >
                        Opret afgang
                    </Button>
                </Group>
            </Group>

            <Paper withBorder p="md" radius="md" mb="md">
                <Stack gap="sm">
                    <Group wrap="wrap" align="end">
                        <Stack gap={6}>
                            <Text size="sm" fw={500}>Dato</Text>

                            <Chip.Group
                                multiple={false}
                                value={filterDate}
                                onChange={(value) => setFilterDate(value || "all")}
                            >
                                <Group gap="xs">
                                    <Chip value="all">Alle</Chip>

                                    {dateOptions.map((date) => (
                                        <Chip key={date} value={date}>
                                            {date}
                                        </Chip>
                                    ))}
                                </Group>
                            </Chip.Group>
                        </Stack>
                    </Group>

                    <Group wrap="wrap" align="end">
                        <Select
                            label="Rute"
                            data={filterRouteOptions}
                            value={filterRouteId}
                            onChange={setFilterRouteId}
                            searchable
                            w={260}
                        />

                        <Select
                            label="Status"
                            data={filterStatusOptions}
                            value={filterStatus}
                            onChange={setFilterStatus}
                            w={180}
                        />

                        <Select
                            label="Aktiv"
                            data={filterActiveOptions}
                            value={filterActive}
                            onChange={setFilterActive}
                            w={180}
                        />

                        <TextInput
                            label="Søg"
                            placeholder="Søg i rute, dato, tid..."
                            value={search}
                            onChange={(e) => setSearch(e.currentTarget.value)}
                            leftSection={<IconSearch size={16} />}
                            w={260}
                        />

                        <Button
                            variant="light"
                            color="gray"
                            leftSection={<IconX size={16} />}
                            onClick={resetFilters}
                        >
                            Nulstil
                        </Button>
                    </Group>

                    <Text size="sm" c="dimmed">
                        Viser {filteredDepartures.length} af {departures.length} afgange
                    </Text>
                </Stack>
            </Paper>

            <Paper withBorder p="md" radius="md">
                <Table.ScrollContainer minWidth={1000}>
                    <Table striped highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Rute</Table.Th>
                                <Table.Th>Dato</Table.Th>
                                <Table.Th>Start</Table.Th>
                                <Table.Th>Kapacitet</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Aktiv</Table.Th>
                                <Table.Th></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {filteredDepartures.length > 0 ? (
                                filteredDepartures.map((departure) => (
                                    <Table.Tr key={departure.transportDepartureID}>
                                        <Table.Td>{departure.routeName}</Table.Td>
                                        <Table.Td>{departure.departureDate}</Table.Td>
                                        <Table.Td>{departure.startTime}</Table.Td>
                                        <Table.Td>{departure.capacity}</Table.Td>
                                        <Table.Td>{departure.status}</Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={departure.isActive ? "green" : "gray"}
                                                variant="light"
                                            >
                                                {departure.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Group justify="flex-end" gap="xs">
                                                <Tooltip label="Rediger">
                                                    <ActionIcon
                                                        variant="light"
                                                        onClick={() => openEdit(departure)}
                                                    >
                                                        <IconPencil size={16} />
                                                    </ActionIcon>
                                                </Tooltip>

                                                <Tooltip label="Kopiér">
                                                    <ActionIcon
                                                        variant="light"
                                                        onClick={() => openCopy(departure)}
                                                    >
                                                        <IconCopy size={16} />
                                                    </ActionIcon>
                                                </Tooltip>

                                                <Tooltip label="Slet">
                                                    <ActionIcon
                                                        color="red"
                                                        variant="light"
                                                        onClick={() =>
                                                            onDeleteDeparture(
                                                                departure.transportDepartureID
                                                            )
                                                        }
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                ))
                            ) : (
                                <Table.Tr>
                                    <Table.Td colSpan={7}>
                                        <Text ta="center" c="dimmed" py="md">
                                            Ingen afgange matcher filtrene
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            <Modal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? "Rediger afgang" : "Opret afgang"}
                centered
            >
                <Stack>
                    <Select
                        label="Rute"
                        data={routeOptions}
                        value={transportRouteId}
                        onChange={setTransportRouteId}
                        searchable
                        disabled={!!editing}
                    />

                    <TextInput
                        label="Dato (yyyy-MM-dd)"
                        value={departureDate}
                        onChange={(e) => setDepartureDate(e.currentTarget.value)}
                    />

                    <TextInput
                        label="Starttid (HH:mm)"
                        value={startTime}
                        onChange={(e) => setStartTime(e.currentTarget.value)}
                    />

                    <NumberInput
                        label="Kapacitet"
                        value={capacity}
                        onChange={(v) => setCapacity(Number(v) || 1)}
                        min={1}
                    />

                    {editing && (
                        <>
                            <Select
                                label="Status"
                                data={[
                                    { value: "active", label: "active" },
                                    { value: "cancelled", label: "cancelled" },
                                    { value: "closed", label: "closed" },
                                ]}
                                value={status}
                                onChange={(v) => setStatus(v ?? "active")}
                            />

                            <Switch
                                label="Aktiv"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.currentTarget.checked)}
                            />
                        </>
                    )}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setModalOpen(false)}>
                            Annullér
                        </Button>
                        <Button onClick={save} loading={saving}>
                            Gem
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={copyModalOpen}
                onClose={() => setCopyModalOpen(false)}
                title="Kopiér afgang"
                centered
            >
                <Stack>
                    <Text size="sm" c="dimmed">
                        {copySource
                            ? `${copySource.routeName} • ${copySource.departureDate} • ${copySource.startTime}`
                            : ""}
                    </Text>

                    <TextInput
                        label="Ny dato (yyyy-MM-dd)"
                        value={copyDepartureDate}
                        onChange={(e) => setCopyDepartureDate(e.currentTarget.value)}
                    />

                    <TextInput
                        label="Ny starttid (HH:mm)"
                        value={copyStartTime}
                        onChange={(e) => setCopyStartTime(e.currentTarget.value)}
                    />

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setCopyModalOpen(false)}>
                            Annullér
                        </Button>
                        <Button onClick={copyDeparture} loading={copySaving}>
                            Kopiér
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={bulkModalOpen}
                onClose={() => setBulkModalOpen(false)}
                title="Masseopret afgange"
                centered
            >
                <Stack>
                    <Select
                        label="Rute"
                        data={routeOptions}
                        value={bulkTransportRouteId}
                        onChange={setBulkTransportRouteId}
                        searchable
                    />

                    <TextInput
                        label="Dato (yyyy-MM-dd)"
                        value={bulkDepartureDate}
                        onChange={(e) => setBulkDepartureDate(e.currentTarget.value)}
                    />

                    <Group grow>
                        <TextInput
                            label="Fra tid (HH:mm)"
                            value={bulkStartTime}
                            onChange={(e) => setBulkStartTime(e.currentTarget.value)}
                        />

                        <TextInput
                            label="Til tid (HH:mm)"
                            value={bulkEndTime}
                            onChange={(e) => setBulkEndTime(e.currentTarget.value)}
                        />
                    </Group>

                    <NumberInput
                        label="Interval minutter"
                        value={bulkIntervalMinutes}
                        onChange={(v) => setBulkIntervalMinutes(Number(v) || 60)}
                        min={1}
                    />

                    <NumberInput
                        label="Kapacitet"
                        value={bulkCapacity}
                        onChange={(v) => setBulkCapacity(Number(v) || 1)}
                        min={1}
                    />

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setBulkModalOpen(false)}>
                            Annullér
                        </Button>
                        <Button onClick={bulkCreateDepartures} loading={bulkSaving}>
                            Opret
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}